/**
 * 阴影进度条组件，在子组件上覆盖一个mask，表示进度
 *
 * PS: 需要设置父元素positon为relative
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { classnames } from '@/utils/tools';

import './style.scss';

export type Ref = HTMLDivElement;
export type NumberTuple = [number, number];
export interface MaskProcessProps {
  /** 进度，旋转百分比，0 ~ 100 */
  value?: number;
  /** 自定义类名 */
  className?: string;
}

const PREFIX = 'mask-process-container';

const MaskProcess = React.forwardRef<Ref, MaskProcessProps>((props, ref) => {
  const { value, className = '' } = props;

  const [containerAttrs, setContainerAttrs] = useState({ width: 0, height: 0 }); // 元素宽高
  const radius = useMemo(() => Math.sqrt(Math.pow(containerAttrs.width / 2, 2) + Math.pow(containerAttrs.height / 2, 2)), [containerAttrs]); // 圆的半径

  const getElementAttrs = useCallback((ele) => {
    const { clientWidth, clientHeight } = ele;

    setContainerAttrs({ width: clientWidth, height: clientHeight });
  }, [ref, value]);

  /**
   * @description 获取阴影的路径
   */
  const getMaskPath = useCallback(() => {
    const { width, height } = containerAttrs;
    const centerX = width / 2; // 圆心X
    const centerY = height / 2; // 圆心Y
    // 百分比转换为弧度
    const angle = (((value as number) / 100) * 360 * Math.PI) / 180;
    const [endPointX, endPointY] = getPointCoordinate(centerX, centerY, angle); // 终点坐标

    if (value === 0) {
      return `M 0 0 H ${width} V ${width} H 0 L 0 0`;
    }

    return `M ${centerX} ${centerY} L ${width / 2} ${centerX - radius} A ${radius} ${radius} 0 1 0 ${endPointX} ${endPointY} Z`;
  }, [radius, value]);

  /**
   * @description 获得圆上点的坐标
   * @param {number} x偏移量
   * @param {number} y偏移量
   * @param {number} 旋转过的角度
   */
  const getPointCoordinate = useCallback((offsetX, offsetY, angle) => {
    const x = offsetX + radius * Math.sin(angle);
    const y = offsetY - radius * Math.cos(angle);

    return [x, y];
  }, [radius]);

  useEffect(() => {
    if (ref !== null && typeof ref !== 'function') {
      getElementAttrs(ref.current);
    }
  }, []);

  return (
    <div ref={ref} className={classnames(PREFIX, { [className]: className })}>
      <svg className="mask-content">
        <path className={classnames({ 'mask-content-path': value !== 0 })} fill="rgba(0,0,0,0.1)" d={getMaskPath()} />
      </svg>
    </div>
  );
});

export default MaskProcess;
